/**
 * Export links (docs/MCP.md "Export links"): a tool that would answer more
 * rows than an AI app should read inline answers a short-lived download link
 * instead — an MCP `resource_link` — and the file streams when the link is
 * opened. These are the shapes and the file formats every export shares; the
 * link itself is signed in src/adapters/mcp/export-link.ts, and what each
 * export reads is registered in src/adapters/mcp/exports.ts.
 */
import { z } from 'zod'

/** How long a link works. Long enough for an AI app's code tool to fetch it; no longer. */
export const EXPORT_LINK_MINUTES = 15
/** The most rows one export holds — past it, narrow the filters. */
export const MAX_EXPORT_ROWS = 200_000
/** Rows read from the database per step while a file streams. */
export const EXPORT_BATCH_SIZE = 200

export const exportFormats = ['csv', 'ndjson'] as const
export type ExportFormat = (typeof exportFormats)[number]

export const exportMimeTypes: Record<ExportFormat, string> = {
  csv: 'text/csv',
  ndjson: 'application/x-ndjson',
}

/** What every export tool answers: the link, never the rows. */
export const exportResultSchema = z.object({
  url: z.string(),
  expiresAt: z.iso.datetime(),
  format: z.enum(exportFormats),
  mimeType: z.string(),
  /** How many rows the file will hold — at least, when `rowsAtLeast` says so. */
  rows: z.number().int().min(0),
  /** True when the count stopped at a scan limit, so the file may hold more. */
  rowsAtLeast: z.boolean(),
  fields: z.array(z.string()),
})
export type ExportResult = z.infer<typeof exportResultSchema>

/** One CSV value: quoted when it must be, a formula-looking start defused (CSV injection). */
function csvValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  let text = typeof value === 'object' ? JSON.stringify(value) : String(value)
  // A leading = + - @ makes Excel run the cell as a formula. A number is a number.
  if (/^[=+\-@\t\r]/.test(text) && typeof value !== 'number') text = `'${text}`
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

/** One CSV line, CRLF-terminated as RFC 4180 and Excel expect. */
export function csvLine(values: readonly unknown[]): string {
  return `${values.map(csvValue).join(',')}\r\n`
}

/** One NDJSON line holding only the chosen fields, in their order. */
export function ndjsonLine(record: Record<string, unknown>, fields: readonly string[]): string {
  return `${JSON.stringify(Object.fromEntries(fields.map((field) => [field, record[field] ?? null])))}\n`
}
